import React, {Component} from 'react'
import {
  ChakraProvider,
  extendTheme,
  Box,
  Spinner,
  Center
} from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';

import Authentication from './subsystems/Authentication/Authentication'
import ForgotPassword from './subsystems/Authentication/ForgotPassword'
import ResetPassword from './subsystems/Authentication/ResetPassword'
import Settings from './subsystems/Settings/Settings'
import Dashboard from './subsystems/Main/Dashboard'
import GoalsView from './subsystems/Main/GoalsView'
import AllGoalsView from './subsystems/Main/AllGoalsView'
import CreateGoalPage from './subsystems/Main/CreateGoal'
import PracticeView from './subsystems/Main/PracticeView'
import Tutorial from './subsystems/Main/Tutorial'
import LandingPage from './subsystems/LandingPage/LandingPage'
import ToastProvider from './components/ToastProvider'
import toastService from './utils/toastService'
import UpgradeModal from './components/UpgradeModal'
import API_URL from './config'

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: '#f0f9ff',
      100: '#e0f2fe', 
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    purple: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff', 
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    }
  },
  fonts: {
    heading: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
  },
  styles: {
    global: {
      body: {
        bg: 'gray.50',
        color: 'gray.800'
      }
    }
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
      variants: {
        solid: {
          borderRadius: 'lg',
          fontWeight: '600',
          _hover: {
            transform: { base: 'none', md: 'translateY(-1px)' },
            boxShadow: { base: 'md', md: 'lg' },
          },
          _active: {
            transform: 'scale(0.98)'
          },
          transition: 'all 0.2s'
        },
        outline: {
          borderRadius: 'lg',
          fontWeight: '600',
          _hover: {
            transform: { base: 'none', md: 'translateY(-1px)' },
            boxShadow: { base: 'sm', md: 'md' },
          },
          _active: {
            transform: 'scale(0.98)'
          },
          transition: 'all 0.2s'
        }
      },
      sizes: {
        xl: {
          h: { base: '52px', md: '56px' },
          minW: { base: '140px', md: '160px' },
          fontSize: { base: 'lg', md: 'xl' },
          px: { base: 5, md: 8 }
        },
        lg: {
          h: { base: '44px', md: '48px' },
          minW: { base: '120px', md: '140px' },
          fontSize: { base: 'md', md: 'lg' },
          px: { base: 4, md: 6 }
        },
        md: {
          h: { base: '36px', md: '40px' },
          minW: { base: '100px', md: '120px' },
          fontSize: { base: 'sm', md: 'md' },
          px: { base: 3, md: 4 }
        },
        sm: {
          h: { base: '28px', md: '32px' },
          minW: { base: '80px', md: '100px' },
          fontSize: { base: 'xs', md: 'sm' },
          px: { base: 2, md: 3 }
        }
      }
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: 'xl',
          boxShadow: 'sm',
          border: '1px',
          borderColor: 'gray.200',
          transition: 'all 0.2s',
          _hover: {
            boxShadow: 'md',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    Input: {
      sizes: {
        lg: {
          field: {
            h: { base: '44px', md: '48px' },
            fontSize: { base: 'md', md: 'lg' }
          }
        },
        md: {
          field: {
            h: { base: '36px', md: '40px' },
            fontSize: { base: 'sm', md: 'md' }
          }
        }
      }
    },
    Textarea: {
      sizes: {
        lg: {
          fontSize: { base: 'md', md: 'lg' },
          p: { base: 3, md: 4 }
        },
        md: {
          fontSize: { base: 'sm', md: 'md' },
          p: { base: 2, md: 3 }
        }
      }
    }
  }
})

function App() {
  return (
    <ChakraProvider theme={theme}>
      <ToastProvider>
        <Router>
          <Box minH="100vh" bg="gray.50">
            <Routes>
            <Route path="/" element={<AppWithRouter />} />
            <Route path="/landing" element={<AppWithRouter />} />
            <Route path="/login" element={<AppWithRouter />} />
            <Route path="/register" element={<AppWithRouter />} />
            <Route path="/forgot-password" element={<AppWithRouter />} />
            <Route path="/reset-password" element={<AppWithRouter />} />
            <Route path="/settings" element={<AppWithRouter />} />
            <Route path="/dashboard" element={<AppWithRouter />} />
            <Route path="/goals" element={<AppWithRouter />} />
            <Route path="/goals/:goalId" element={<AppWithRouter />} />
            <Route path="/all-goals" element={<AppWithRouter />} />
            <Route path="/create-goal" element={<AppWithRouter />} />
            <Route path="/tutorial" element={<AppWithRouter />} />
            <Route path="/practice/:exerciseId" element={<AppWithRouter />} />
            <Route path="*" element={<Navigate to="/landing" replace />} />
          </Routes>
        </Box>
      </Router>
      </ToastProvider>
    </ChakraProvider>
  );
}
const initialState = {
  username: "",
  email: "",
  password: "",
  route: "landing",
  mode: "base",
  overview: {},
  loading: false,
  sidebarCollapsed: false,
  goalDetails: {
    goal: "",
    context: "",
    time: "10 mins per day",
    practiceContext: "",
    availableResources: "",
    learning_style: null,
    task_preference: null,
    interaction_style: null
  },
  todaysPractice: null,
  currentGoalId: null,
  practiceStreak: 0,
  userStats: {
    streak: 0,
    total_exercises: 0,
    completed_exercises: 0,
    completion_rate: 0,
    goals_count: 0,
    recent_completions: 0
  },
  searchResults: null,
  searchQuery: "",
  usageStats: null,
  upgradeModalOpen: false,
  upgradeReason: ""
}



// Wrapper component to provide router hooks to class component
function AppWithRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  return (
    <AppUI 
      navigate={navigate} 
      location={location} 
      params={params}
    />
  );
}

class AppUI extends Component {

  constructor(props) {
    super(props)
    // Restore session from localStorage if available
    const savedSession = localStorage.getItem('grow_session');
    let restoredState = {};
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        restoredState = {
          username: session.username || '',
          email: session.email || '',
        };
      } catch (e) {
        localStorage.removeItem('grow_session');
      }
    }
    this.state = {
      ...initialState,
      ...restoredState,
      route: this.getRouteFromPath(props.location.pathname)
    }
  }

  componentDidMount() {
    // Set initial route based on URL
    const route = this.getRouteFromPath(this.props.location.pathname);
    if (route !== this.state.route) {
      this.setState({ route });
    }

    // If we have a restored session, reload user data
    if (this.state.username) {
      this.restoreSession();
    }

    // If landing on a goal URL, load the goal data (requires auth)
    if (this.props.location.pathname.startsWith('/goals/')) {
      const goalId = this.props.location.pathname.split('/goals/')[1];
      if (goalId && this.state.username) {
        this.onContinueJourney(parseInt(goalId));
      } else if (!this.state.username) {
        this.onRouteChange('login');
      }
    }
  }

  restoreSession = async () => {
    try {
      const response = await fetch(`${API_URL}/get_overview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: this.state.username })
      });
      const data = await response.json();
      if (data.success) {
        this.setState({ overview: data.result });
        this.getUsageStats();
      } else {
        // Session invalid, clear it
        localStorage.removeItem('grow_session');
        this.setState({ ...initialState, route: 'login' });
        this.props.navigate('/login');
      }
    } catch (error) {
      console.error('Session restore error:', error);
    }
  }

  componentDidUpdate(prevProps) {
    // Update route when URL changes (browser back/forward)
    if (prevProps.location.pathname !== this.props.location.pathname) {
      const route = this.getRouteFromPath(this.props.location.pathname);
      this.setState({ route });

      // When navigating to a goal URL, load the goal data
      if (this.props.location.pathname.startsWith('/goals/')) {
        const goalId = this.props.location.pathname.split('/goals/')[1];
        if (goalId && this.state.username) {
          this.onContinueJourney(parseInt(goalId));
        } else if (!this.state.username) {
          this.onRouteChange('login');
        }
      }
    }
  }

  getRouteFromPath = (pathname) => {
    if (pathname === '/' || pathname === '/landing') return 'landing';
    if (pathname === '/login') return 'login';
    if (pathname === '/register') return 'register';
    if (pathname === '/forgot-password') return 'forgotPassword';
    if (pathname === '/reset-password') return 'resetPassword';
    if (pathname === '/settings') return 'settings';
    if (pathname === '/dashboard') return 'dashboard';
    if (pathname.startsWith('/goals/')) return 'practicesView';
    if (pathname === '/all-goals') return 'allGoalsView';
    if (pathname === '/create-goal') return 'createGoal';
    if (pathname === '/tutorial') return 'tutorial';
    if (pathname.startsWith('/practice/')) return 'practice';
    return 'landing';
  }

  getPathFromRoute = (route, params = {}) => {
    switch (route) {
      case 'landing': return '/landing';
      case 'login': return '/login';
      case 'register': return '/register';
      case 'forgotPassword': return '/forgot-password';
      case 'resetPassword': return '/reset-password';
      case 'settings': return '/settings';
      case 'dashboard': return '/dashboard';
      case 'practicesView':
        return params.goalId ? `/goals/${params.goalId}` : '/all-goals';
      case 'allGoalsView': return '/all-goals';
      case 'createGoal': return '/create-goal';
      case 'tutorial': return '/tutorial';
      case 'practice':
        return params.exerciseId ? `/practice/${params.exerciseId}` : '/practice';
      default: return '/landing';
    }
  }

  setLoading = (loading) => {
    this.setState({ loading })
  }

  // colorScheme = this.state.mode == 'history' ? 'purple' : 'black'

  onNameChange = (value) => {
      this.setState({
        username: value
      })
  }

  onEmailChange = (value) => {
    this.setState({
      email: value
    })
}

  onPasswordChange = (value) => {
    this.setState({
      password: value
    })
  }

  onRouteChange = (dest, params = {}) => {
    const path = this.getPathFromRoute(dest, params);
    this.props.navigate(path);

    // Clear session on logout (navigating to login or landing)
    if (dest === 'login' || dest === 'landing') {
      localStorage.removeItem('grow_session');
      this.setState({ ...initialState, route: dest });
      return;
    }

    this.setState({
      route: dest,
      searchResults: null, // Clear search results when navigating
      searchQuery: "", // Clear search query when navigating
      ...params
    });
  }

  toggleSidebar = () => {
    this.setState({
      sidebarCollapsed: !this.state.sidebarCollapsed
    })
  }

  getUsageStats = async () => {
    if (!this.state.username) return;

    try {
      const response = await fetch(`${API_URL}/get_usage_stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: this.state.username })
      });
      const data = await response.json();
      if (data.success) {
        this.setState({ usageStats: data.result });
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  }

  onFormTextChange = (form, key, value) => {
    
    
    this.setState({
      [form]: {
        ...this.state[form],
        [key]: value
      }
    }, () => {
      
    })
  }

  onPaginationButtonClick = (value, loc) => {
    let page = this.state[loc].page
    let add = 0
    if (value === 'Next') {
      add += 1
    } else {
      add += -1
    }
    this.setState({
      [loc]: {
        ...this.state[loc],
        page: page += add
      }
    }) 
  }

  onFormTextChangeNested4 = (level_1, level_2, key, value) => {
  //   this.setState({
  //     [level_1]: {
  //       ...this.state[level_1],
  //       [level_2]: {
  //         ...this.state[level_2],
  //         [key]: value
  //       }
  //     }
  //   })
  // }
    this.setState({
      [level_1]: {
        ...this.state[level_1],
        [level_2]: {
          commenting: true,
          [key]: value
        }
      }
    })
  }

  handleModeChange = async (idx) => {
    let mode = idx === 0 ? 'history' : 'science'
    this.setState({
      ...this.state,
      mode: mode
    })
  }

  onRegister = async () => {
    this.setLoading(true)
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username,
          email: this.state.email,
          password: this.state.password
        })
      }
      let response = await (await fetch(`${API_URL}/register`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        localStorage.setItem('grow_session', JSON.stringify({
          username: this.state.username,
          email: this.state.email
        }));
        toastService.success('Registration Successful', message)
        this.onRouteChange('dashboard');
      } else {
        toastService.error('Registration Failed', message)
      }
    } catch (error) {
      console.error('Registration error:', error)
      toastService.networkError('Registration Failed', 'Please check your connection and try again.')
    } finally {
      this.setLoading(false)
    }
  }

  checkSignIn = async () => {
    this.setLoading(true)
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: this.state.username,
          password: this.state.password
        })
      }
      let response = await (await fetch(`${API_URL}/check_signin`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        
        localStorage.setItem('grow_session', JSON.stringify({
          username: this.state.username,
          email: response['email'] || ''
        }));
        toastService.success('Login Successful', message)
        // Fetch usage stats after successful login
        this.getUsageStats();
        this.onRouteChange('dashboard');
        this.setState({
          email: response['email'] || ''
        });
      } else {
        toastService.error('Login Failed', message)
      }
    } catch (error) {
      console.error('Login error:', error)
      toastService.networkError('Login Failed', 'Please check your connection and try again.')
    } finally {
      this.setLoading(false)
    }
  }

  onCreateJourney = async () => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username,
          goalDetails: this.state.goalDetails
        })
      }
      let response = await (await fetch(`${API_URL}/create_goal`, requestOptions)).json()
      let {success, message, result, goal_id} = response
      if (success === true) {
        toastService.success('Goal Created', message)
        // Fetch updated usage stats
        this.getUsageStats();
        // Navigate directly to the newly created goal
        if (goal_id) {
          this.onContinueJourney(goal_id)
        } else {
          // Fallback to dashboard if no goal_id
          this.onRouteChange('dashboard');
          this.setState({
            overview: result
          });
        }
      } else {
        // Check if it's a limit error
        if (result && result.limit_exceeded) {
          this.setState({
            upgradeModalOpen: true,
            upgradeReason: message
          });
        } else {
          toastService.error('Goal Creation Failed', message)
        }
      }
    } catch (error) {
      console.error('Create goal error:', error)
      toastService.networkError('Goal Creation Failed', 'Please check your connection and try again.')
      throw error
    }
  }

  getOverview = async (showLoading = true) => {
    if (showLoading) {
      this.setLoading(true)
    }
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/get_overview`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        this.setState({
          overview: result
        });
      } else {
        if (showLoading) {
          toastService.error('Error', message)
        }
      }
    } catch (error) {
      console.error('Get overview error:', error)
      if (showLoading) {
        toastService.networkError('Failed to load goals', 'Please check your connection and try again.')
      }
    } finally {
      if (showLoading) {
        this.setLoading(false)
      }
    }
  }

  onContinueJourney = async (id) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/on_continue_goal`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        this.onRouteChange('practicesView', { goalId: id });
        this.setState({
          overview: result,
          currentGoalId: id
        });
      } else {
        toastService.error('Error', message)
      }
    } catch (error) {
      console.error('Continue journey error:', error)
      toastService.networkError('Failed to load goal', 'Please check your connection and try again.')
    }
  }

  onDeleteJourney = async (id) => {
    this.setLoading(true)
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/on_delete_goal`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        
        toastService.success('Goal Deleted', 'Goal has been successfully deleted')
        this.onRouteChange('dashboard');
        this.setState({
          overview: result
        });
      } else {
        toastService.error('Delete Failed', message)
      }
    } catch (error) {
      console.error('Delete journey error:', error)
      toastService.networkError('Delete Failed', 'Please check your connection and try again.')
    } finally {
      this.setLoading(false)
    }
  }

  onContinueChapter = async (id) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/on_continue_exercise`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        this.onRouteChange('practice', { exerciseId: id });
        this.setState({
          page: result
        });
      } else {
        toastService.error('Error', message)
      }
    } catch (error) {
      console.error('Continue chapter error:', error)
      toastService.networkError('Failed to load exercise', 'Please check your connection and try again.')
    }
  }

  onDeleteChapter = async (id) => {
    this.setLoading(true)
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          goal_id: this.state.currentGoalId,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/on_delete_exercise`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        toastService.success('Exercise Deleted', 'Exercise has been successfully deleted')
        this.onRouteChange('practicesView', { goalId: this.state.currentGoalId });
        this.setState({
          overview: result
        });
      } else {
        toastService.error('Delete Failed', message)
      }
    } catch (error) {
      console.error('Delete chapter error:', error)
      toastService.networkError('Delete Failed', 'Please check your connection and try again.')
    } finally {
      this.setLoading(false)
    }
  }

  onRandomChapter = async () => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/get_overview`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        if (result.exists && result.items.length > 0) {
          // Get a random chapter from all available chapters
          const randomJourney = result.items[Math.floor(Math.random() * result.items.length)]
          // Continue to that journey's chapters
          const journeyResponse = await (await fetch(`${API_URL}/on_continue_goal`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              id: randomJourney.id,
              username: this.state.username
            })
          })).json()
          
          if (journeyResponse.success) {
            const chapters = journeyResponse.result
            if (chapters.exists && chapters.items.length > 0) {
              // Select a random chapter
              const randomChapter = chapters.items[Math.floor(Math.random() * chapters.items.length)]
              // Navigate to that chapter
              this.onContinueChapter(randomChapter.id)
            }
          }
        } else {
          toastService.info('No Practices Available', 'Create some goals first to use random practice feature.')
        }
      } else {
        toastService.error('Error', message)
      }
    } catch (error) {
      console.error('Random chapter error:', error)
      toastService.networkError('Failed to find random practice', 'Please check your connection and try again.')
    }
  }

  onCreateExercise = async () => {
    this.setLoading(true)
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username,
          exerciseDetails: this.state.exerciseDetails,
          goalId: this.state.currentGoalId // Use the current goal ID
        })
      }
      let response = await (await fetch(`${API_URL}/create_exercise`, requestOptions)).json()
      let {success, message, result} = response
      if (success === true) {
        toastService.success('Exercise Created', message)
        this.setState({
          overview: result,
          route: 'practicesView'
        })
      } else {
        if (result && result.limit_exceeded) {
          this.setState({
            upgradeModalOpen: true,
            upgradeReason: message
          });
        } else {
          toastService.error('Exercise Creation Failed', message)
        }
      }
    } catch (error) {
      console.error('Create exercise error:', error)
      toastService.networkError('Exercise Creation Failed', 'Please check your connection and try again.')
    } finally {
      this.setLoading(false)
    }
  }

  onGenerateExercise = async (goalId) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username,
          goalId: goalId
        })
      }
      let response = await (await fetch(`${API_URL}/generate_progressive_exercise`, requestOptions)).json()
      let {success, message, result} = response
      if (success === true) {
        toastService.success('Exercise Generated', message)
        this.setState({
          overview: result,
          route: 'practicesView'
        })
      } else {
        if (result && result.limit_exceeded) {
          this.setState({
            upgradeModalOpen: true,
            upgradeReason: message
          });
        } else {
          toastService.error('Exercise Generation Failed', message)
        }
      }
    } catch (error) {
      console.error('Generate exercise error:', error)
      toastService.networkError('Exercise Generation Failed', 'Please check your connection and try again.')
      throw error
    }
  }

  getUserStats = async (showGlobalLoading = false) => {
    if (showGlobalLoading) {
      this.setLoading(true)
    }
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/get_user_stats`, requestOptions)).json()
      let {success, result} = response
      if (success) {
        this.setState({
          userStats: result,
          practiceStreak: result.streak
        })
      }
    } catch (error) {
      console.error('Error fetching user stats:', error)
      // Only show error toast if this was a user-initiated action
      if (showGlobalLoading) {
        toastService.networkError('Failed to load statistics', 'Unable to fetch your progress data.')
      }
    } finally {
      if (showGlobalLoading) {
        this.setLoading(false)
      }
    }
  }

  onSearch = async (query, type = 'all', showGlobalLoading = false) => {
    if (!query.trim()) {
      this.setState({ searchResults: null })
      return
    }

    if (showGlobalLoading) {
      this.setLoading(true)
    }
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username,
          query: query,
          type: type
        })
      }
      let response = await (await fetch(`${API_URL}/search`, requestOptions)).json()
      let {success, result} = response
      if (success) {
        this.setState({
          searchResults: result,
          searchQuery: query
        })
      } else {
        if (showGlobalLoading) {
          toastService.error('Search Failed', 'No results found for your search.')
        }
      }
    } catch (error) {
      console.error('Search error:', error)
      if (showGlobalLoading) {
        toastService.networkError('Search Failed', 'Please check your connection and try again.')
      }
    } finally {
      if (showGlobalLoading) {
        this.setLoading(false)
      }
    }
  }

  onToggleExerciseCompletion = async (exerciseId, completed) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: exerciseId,
          completed: completed,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/toggle_exercise_completion`, requestOptions)).json()
      let {success, message, result} = response
      if (success) {
        toastService.success('Exercise Updated', message)
        // Update streak in state
        this.setState({
          practiceStreak: result.streak
        })
        // Refresh user stats
        this.getUserStats()
      } else {
        toastService.error('Update Failed', message)
      }
    } catch (error) {
      console.error('Toggle exercise completion error:', error)
      toastService.networkError('Update Failed', 'Please check your connection and try again.')
    }
  }

  onNavigateExercise = async (exerciseId, direction) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exercise_id: exerciseId,
          direction: direction,
          username: this.state.username
        })
      }
      let response = await (await fetch(`${API_URL}/navigate_exercise`, requestOptions)).json()
      let {success, result} = response
      if (success && result.items && result.items.length > 0) {
        // Navigate to the new exercise
        const newExercise = result.items[0]
        this.onContinueChapter(newExercise.id)
      } else {
        toastService.error('Navigation Failed', `No ${direction} exercise available`)
      }
    } catch (error) {
      console.error('Navigate exercise error:', error)
      toastService.networkError('Navigation Failed', 'Please check your connection and try again.')
    }
  }

  render() {
    if (this.state.loading) {
      return (
        <Center h="100vh">
          <Box textAlign="center">
            <Spinner size="xl" color="brand.500" thickness="4px" />
            <Box mt={4} fontSize="lg" color="gray.600">
              Loading...
            </Box>
          </Box>
        </Center>
      )
    }

    return (
      <Box minH="100vh">
        {this.state.route === 'landing' && (
          <LandingPage
            onRouteChange={this.onRouteChange}
            state={this.state}
          />
        )}
        
        {this.state.route === 'dashboard' && (
          <Dashboard
            state={this.state}
            onPaginationButtonClick={this.onPaginationButtonClick}
            onRouteChange={this.onRouteChange}
            onFormTextChange={this.onFormTextChange}
            onCreateJourney={this.onCreateJourney}
            onContinueJourney={this.onContinueJourney}
            onDeleteJourney={this.onDeleteJourney}
            onContinueChapter={this.onContinueChapter}
            getOverview={this.getOverview}
            getUserStats={this.getUserStats}
            onSearch={this.onSearch}
            handleModeChange={this.handleModeChange}
            onRandomChapter={this.onRandomChapter}
            toggleSidebar={this.toggleSidebar}
            setLoading={this.setLoading}
          />
        )}
        
        {this.state.route === 'practicesView' && (
          <GoalsView
            state={this.state}
            onPaginationButtonClick={this.onPaginationButtonClick}
            onRouteChange={this.onRouteChange}
            onFormTextChange={this.onFormTextChange}
            onCreateJourney={this.onCreateJourney}
            onContinueJourney={this.onContinueJourney}
            onContinueChapter={this.onContinueChapter}
            onDeleteChapter={this.onDeleteChapter}
            getOverview={this.getOverview}
            handleModeChange={this.handleModeChange}
            onRandomChapter={this.onRandomChapter}
            onGenerateExercise={this.onGenerateExercise}
            toggleSidebar={this.toggleSidebar}
            setLoading={this.setLoading}
          />
        )}
        
        {this.state.route === 'allGoalsView' && (
          <AllGoalsView
            state={this.state}
            onRouteChange={this.onRouteChange}
            onContinueJourney={this.onContinueJourney}
            onDeleteJourney={this.onDeleteJourney}
            getOverview={this.getOverview}
            getUserStats={this.getUserStats}
            onSearch={this.onSearch}
            onRandomChapter={this.onRandomChapter}
            toggleSidebar={this.toggleSidebar}
            setLoading={this.setLoading}
          />
        )}
        
        {this.state.route === 'createGoal' && (
          <CreateGoalPage
            state={this.state}
            onPaginationButtonClick={this.onPaginationButtonClick}
            onRouteChange={this.onRouteChange}
            onFormTextChange={this.onFormTextChange}
            onCreateJourney={this.onCreateJourney}
            getOverview={this.getOverview}
            handleModeChange={this.handleModeChange}
            onRandomChapter={this.onRandomChapter}
            toggleSidebar={this.toggleSidebar}
            setLoading={this.setLoading}
          />
        )}
        
        {this.state.route === 'tutorial' && (
          <Tutorial
            onRouteChange={this.onRouteChange}
          />
        )}
        
        {this.state.route === 'practice' && (
          <PracticeView
            state={this.state}
            onPaginationButtonClick={this.onPaginationButtonClick}
            onRouteChange={this.onRouteChange}
            onFormTextChange={this.onFormTextChange}
            onCreateJourney={this.onCreateJourney}
            getOverview={this.getOverview}
            onContinueChapter={this.onContinueChapter}
            onNavigateExercise={this.onNavigateExercise}
            onToggleExerciseCompletion={this.onToggleExerciseCompletion}
            handleModeChange={this.handleModeChange}
            onRandomChapter={this.onRandomChapter}
            toggleSidebar={this.toggleSidebar}
            setLoading={this.setLoading}
          />
        )}
        
        {(this.state.route === 'login' || this.state.route === 'register') && (
          <Authentication
            onNameChange={this.onNameChange}
            onEmailChange={this.onEmailChange}
            onPasswordChange={this.onPasswordChange}
            onRegister={this.onRegister}
            checkSignIn={this.checkSignIn}
            onRouteChange={this.onRouteChange}
            state={this.state}
            loading={this.state.loading}
          />
        )}

        {this.state.route === 'forgotPassword' && (
          <ForgotPassword onRouteChange={this.onRouteChange} />
        )}

        {this.state.route === 'resetPassword' && (
          <ResetPassword onRouteChange={this.onRouteChange} />
        )}

        {this.state.route === 'settings' && (
          <Settings
            state={this.state}
            onRouteChange={this.onRouteChange}
            onEmailChange={this.onEmailChange}
            toggleSidebar={this.toggleSidebar}
            getOverview={this.getOverview}
            onRandomChapter={this.onRandomChapter}
          />
        )}

        <UpgradeModal
          isOpen={this.state.upgradeModalOpen}
          onClose={() => this.setState({ upgradeModalOpen: false })}
          username={this.state.username}
          reason={this.state.upgradeReason}
        />
      </Box>
    )
  }
}

export default App;
